import React from 'react';

import { Box, CircularProgress, useTheme } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { type StripeElementsOptions } from '@stripe/stripe-js';

import { useStripeInstance } from 'src/hooks/stripe';

interface StripeElementsProps {
  clientSecret: string | null;
  isClientSecretLoading?: boolean;
}

export const StripeElements: React.FC<React.PropsWithChildren<StripeElementsProps>> = ({
  children,
  clientSecret,
  isClientSecretLoading,
}) => {
  const theme = useTheme();
  const { stripeInstance, stripeInstanceLoading, initStripe } = useStripeInstance();

  React.useEffect(() => {
    initStripe();
  }, [initStripe]);

  const stripeOptions = React.useMemo((): StripeElementsOptions | null => {
    if (!clientSecret) return null;
    return {
      clientSecret,
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: theme.palette.primary.main,
          colorBackground: theme.palette.background.default,
          colorDanger: theme.palette.error.main,
          fontFamily: theme.typography.fontFamily,
        },
      },
    };
  }, [clientSecret, theme]);

  if (isClientSecretLoading || stripeInstanceLoading || !stripeInstance) {
    return (
      <Box display="flex" justifyContent="center">
        <CircularProgress size={100} />
      </Box>
    );
  }

  if (!stripeOptions) return null;

  return (
    <Elements stripe={stripeInstance} options={stripeOptions}>
      {children}
    </Elements>
  );
};
